import { FONT_MONO } from 'styles/tokens/typography';
import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Collapse,
  Chip,
  Tooltip,
  InputAdornment
} from '@mui/material';
import MDButton from 'components/MDButton';
import {
  Add,
  Delete,
  ExpandMore,
  ExpandLess,
  DriveFileRenameOutlineOutlined,
  FilterListOutlined,
  CheckCircleOutlined,
  Settings
} from '@mui/icons-material';
import ConditionRow from './ConditionRow';
import { EmptyState } from '../StepSection';
import {
  inputSx,
  cardSx,
  badgeSx,
  optionCardSx,
  statusChipSx,
  statusColors,
  tintSx,
  tintedIconButtonSx,
  FONT
} from '../../styles/wizardSx';

const LOGIC_OPTIONS = [
  { value: 'AND', kind: 'success', helper: 'All must match' },
  { value: 'OR', kind: 'warning', helper: 'Any can match' }
];

function ConditionGroup({ group, groupIndex, onUpdate, onRemove }) {
  const [expanded, setExpanded] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const addCondition = () => {
    const newCondition = {
      id: `condition_${Date.now()}`,
      field: '',
      operator: '',
      value: ''
    };
    onUpdate({
      conditions: [...group.conditions, newCondition]
    });
  };

  const updateCondition = (conditionId, updates) => {
    onUpdate({
      conditions: group.conditions.map(condition =>
        condition.id === conditionId
          ? { ...condition, ...updates }
          : condition
      )
    });
  };

  const removeCondition = (conditionId) => {
    onUpdate({
      conditions: group.conditions.filter(c => c.id !== conditionId)
    });
  };

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        ...cardSx(theme, { highlighted: expanded }),
        overflow: 'hidden',
        transition: 'border-color 0.3s ease'
      })}
    >
      {/* Header */}
      <Box sx={{
        p: 2.5,
        bgcolor: 'surface.main',
        borderBottom: '1px solid',
        borderColor: 'outline.variant',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            {/* Group Number Badge */}
            <Box sx={badgeSx(40)}>
              {groupIndex + 1}
            </Box>

            {/* Group Name */}
            <TextField
              value={group.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder="Group Name"
              inputProps={{ 'aria-label': 'Group name' }}
              sx={[inputSx, { width: '250px' }]}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DriveFileRenameOutlineOutlined sx={{ color: 'primary.main', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Conditions Count */}
            {group.conditions.length > 0 && (
              <Chip
                icon={<FilterListOutlined sx={{ fontSize: 16 }} />}
                label={`${group.conditions.length} condition${group.conditions.length !== 1 ? 's' : ''}`}
                size="small"
                sx={statusChipSx('info')}
              />
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {group.conditions.length > 0 && (
              <Tooltip title="Advanced logic">
                <IconButton
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  aria-label="Advanced logic"
                  sx={tintedIconButtonSx('primary')}
                >
                  <Settings sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
              <IconButton
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? 'Collapse group' : 'Expand group'}
                sx={tintedIconButtonSx('primary')}
              >
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Remove group">
              <IconButton
                onClick={onRemove}
                aria-label="Remove group"
                sx={tintedIconButtonSx('error')}
              >
                <Delete sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Logic Selection - Only show when there are conditions */}
        {group.conditions.length > 1 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: FONT.caption, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Combine conditions with
            </Typography>
            <Box role="radiogroup" aria-label="Combine conditions with" sx={{ display: 'flex', gap: 1 }}>
              {LOGIC_OPTIONS.map((option) => {
                const selected = group.logic === option.value;
                return (
                  <Box
                    key={option.value}
                    role="radio"
                    aria-checked={selected}
                    tabIndex={0}
                    onClick={() => onUpdate({ logic: option.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onUpdate({ logic: option.value });
                      }
                    }}
                    sx={(theme) => ({
                      px: 2,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      ...optionCardSx(theme, { selected, kind: option.kind })
                    })}
                  >
                    {selected && (
                      <CheckCircleOutlined
                        sx={(theme) => ({ fontSize: 16, color: statusColors(theme, option.kind).text })}
                      />
                    )}
                    <Typography
                      sx={(theme) => ({
                        fontSize: FONT.body,
                        fontWeight: 700,
                        color: selected ? statusColors(theme, option.kind).text : 'text.primary'
                      })}
                    >
                      {option.value}
                    </Typography>
                    <Typography sx={{ fontSize: FONT.caption, color: 'text.secondary' }}>
                      {option.helper}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Advanced Logic */}
        <Collapse in={showAdvanced && group.conditions.length > 1}>
          <Box
            sx={[
              { mt: 2, p: 2, borderRadius: '8px', border: '1px solid', borderColor: 'outline.variant' },
              (theme) => tintSx(theme, 0.04)
            ]}
          >
            <Typography sx={{ color: 'primary.main', fontWeight: 600, fontSize: FONT.caption }}>
              Advanced logic (optional)
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={group.customLogic}
              onChange={(e) => onUpdate({ customLogic: e.target.value })}
              placeholder="e.g., (1 AND 2) OR 3"
              sx={[
                inputSx,
                {
                  mt: 1,
                  '& .MuiInputBase-root': {
                    fontFamily: FONT_MONO,
                    fontSize: '13px'
                  }
                }
              ]}
              helperText="Use condition numbers with AND/OR operators"
            />
          </Box>
        </Collapse>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2.5 }}>
          {/* Conditions */}
          {group.conditions.length === 0 ? (
            <EmptyState
              compact
              icon={<FilterListOutlined />}
              title="No conditions in this group"
              action={
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Add />}
                  onClick={addCondition}
                  size="small"
                >
                  Add First Condition
                </MDButton>
              }
            />
          ) : (
            <>
              {group.conditions.map((condition, index) => (
                <Box key={condition.id} sx={{ mb: 2 }}>
                  <ConditionRow
                    condition={condition}
                    conditionIndex={index}
                    groupLogic={group.logic}
                    isLastCondition={index === group.conditions.length - 1}
                    onUpdate={(updates) => updateCondition(condition.id, updates)}
                    onRemove={() => removeCondition(condition.id)}
                  />

                  {/* Logic connector between conditions */}
                  {index < group.conditions.length - 1 && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      my: 2,
                      ml: 6
                    }}>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'outline.variant', mr: 2 }} />
                      <Chip
                        label={group.logic}
                        size="small"
                        sx={[{ height: '22px' }, statusChipSx(group.logic === 'AND' ? 'success' : 'warning')]}
                      />
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'outline.variant', ml: 2 }} />
                    </Box>
                  )}
                </Box>
              ))}

              {/* Add Condition Button */}
              <Box sx={{ mt: 2 }}>
                <MDButton
                  variant="outlined"
                  color="info"
                  startIcon={<Add />}
                  onClick={addCondition}
                  fullWidth
                  sx={{
                    height: '40px',
                    borderRadius: '8px',
                    borderStyle: 'dashed',
                    '&:hover': { borderStyle: 'solid' }
                  }}
                >
                  Add Another Condition
                </MDButton>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

export default ConditionGroup;
